/**
 * BroadcastScreen - Tela de envio de broadcasts (Push/Email)
 *
 * Estrutura refatorada seguindo padrão modular:
 * - core/: Lógica de negócio (constants, utils, hook)
 * - components/: Componentes de UI
 */
import React from 'react'
import { Send, History } from 'lucide-react'

// Core
import { useBroadcastScreen } from './core'

// Components
import {
	BroadcastForm,
	BroadcastTips,
	BroadcastHistoryTable,
	BroadcastPreviewModal,
	BroadcastDetailsModal,
	BroadcastDeleteModal,
	BroadcastResendModal,
} from './components'

// UI Components
import Header from '../components/organisms/Header'
import Button from '../components/atoms/Button'

const BroadcastScreen = () => {
	const {
		// State
		activeTab,
		form,
		history,
		previewStats,

		// Loading states
		loading,
		historyLoading,
		previewLoading,
		sendLoading,

		// Modal states
		showPreview,
		selectedBroadcast,
		showDetails,
		showDelete,
		showResend,

		// Form handlers
		setActiveTab,
		handleFormChange,
		handleCopyToOther,
		handlePreview,
		handleSend,
		resetForm,

		// History handlers
		fetchHistory,

		// Modal handlers
		openDetails,
		closeDetails,
		openDelete,
		closeDelete,
		handleDelete,
		openResend,
		closeResend,
		handleResend,
		handleResendPreview,
		closePreview,
	} = useBroadcastScreen()

	return (
		<div className="min-h-screen">
			<Header
				title="Broadcast"
				subtitle="Envie notificações e e-mails em massa"
			/>

			<div className="p-4 md:p-6">
				{/* Tabs - Full width no mobile */}
				<div className="flex gap-2 mb-4 md:mb-6">
					<Button
						variant={activeTab === 'send' ? 'primary' : 'outline'}
						onClick={() => setActiveTab('send')}
						className="flex-1 md:flex-none"
					>
						<Send className="w-4 h-4 mr-2" />
						<span className="hidden sm:inline">Enviar</span>
					</Button>
					<Button
						variant={activeTab === 'history' ? 'primary' : 'outline'}
						onClick={() => setActiveTab('history')}
						className="flex-1 md:flex-none"
					>
						<History className="w-4 h-4 mr-2" />
						<span className="hidden sm:inline">Histórico</span>
					</Button>
				</div>

				{/* Tab: Enviar Broadcast */}
				{activeTab === 'send' && (
					<div className="flex flex-col lg:grid lg:grid-cols-3 gap-4 md:gap-6">
						{/* Formulário */}
						<div className="lg:col-span-2 order-1">
							<BroadcastForm
								form={form}
								onChange={handleFormChange}
								onCopy={handleCopyToOther}
								onPreview={handlePreview}
								onReset={resetForm}
								loading={loading || previewLoading}
							/>
						</div>

						{/* Dicas - Escondido no mobile, visível em telas maiores */}
						<div className="hidden lg:block lg:col-span-1 order-2">
							<BroadcastTips type={form.type} />
						</div>
					</div>
				)}

				{/* Tab: Histórico */}
				{activeTab === 'history' && (
					<BroadcastHistoryTable
						history={history}
						loading={historyLoading}
						onRefresh={fetchHistory}
						onView={openDetails}
						onResend={openResend}
						onDelete={openDelete}
					/>
				)}
			</div>

			{/* Modal: Preview antes de enviar */}
			<BroadcastPreviewModal
				isOpen={showPreview}
				onClose={closePreview}
				form={form}
				previewStats={previewStats}
				onConfirm={handleSend}
				loading={sendLoading}
			/>

			{/* Modal: Detalhes do broadcast */}
			<BroadcastDetailsModal
				isOpen={showDetails}
				onClose={closeDetails}
				broadcast={selectedBroadcast}
				onResend={() => {
					closeDetails()
					openResend(selectedBroadcast)
				}}
			/>

			{/* Modal: Confirmar exclusão */}
			<BroadcastDeleteModal
				isOpen={showDelete}
				onClose={closeDelete}
				broadcast={selectedBroadcast}
				onConfirm={() => handleDelete(selectedBroadcast?._id)}
				loading={loading}
			/>

			{/* Modal: Reenviar broadcast */}
			<BroadcastResendModal
				isOpen={showResend}
				onClose={closeResend}
				broadcast={selectedBroadcast}
				onConfirm={handleResend}
				onPreview={handleResendPreview}
				loading={sendLoading}
				previewLoading={previewLoading}
				previewStats={previewStats}
			/>
		</div>
	)
}

export default BroadcastScreen
